import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { relativeTime } from '../format';

import type { SupervisionMessage, SupervisionWatcher } from './types';

const ROLE_LABEL: Record<string, string> = {
  shadow_observer: 'acompanhando',
  collaborative_observer: 'colaborando',
  supervisor_monitor: 'acompanhando',
};

/**
 * Painel deslizante que cobre o painel direito enquanto o operador lê as orientações.
 * Fechar devolve o painel exatamente como estava — as abas não são tocadas.
 * Canal separado do histórico com o cliente: nada daqui é mensagem da conversa.
 */
export function SupervisionDrawer({
  watchers,
  messages,
  onClose,
}: {
  watchers: readonly SupervisionWatcher[];
  messages: readonly SupervisionMessage[];
  onClose: () => void;
}): JSX.Element {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="supervision-drawer" role="dialog" aria-label="Orientações da supervisão">
      <div className="supervision-drawer__head">
        <span className="flex-1">Orientações da supervisão</span>
        <button
          ref={closeRef}
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={onClose}
          aria-label="Fechar orientações"
        >
          <X size={14} aria-hidden />
        </button>
      </div>
      {watchers.length > 0 && (
        <div className="supervision-drawer__watchers">
          {watchers
            .map(
              (w) =>
                `${w.supervisorName} ${ROLE_LABEL[w.role] ?? 'acompanhando'} desde ${relativeTime(w.since)}`,
            )
            .join(' · ')}
        </div>
      )}
      {messages.length === 0 ? (
        <p className="supervision-drawer__empty">Nenhuma orientação nesta conversa ainda.</p>
      ) : (
        <ol className="supervision-drawer__list" aria-label="Histórico de orientações">
          {messages.map((m) => (
            <li key={m.id} className="supervision-msg">
              <div className="supervision-msg__meta">
                {m.fromUserName ?? 'Supervisão'} ·{' '}
                <time dateTime={m.timestamp}>{relativeTime(m.timestamp)}</time>
              </div>
              <div className="supervision-msg__text">{m.text}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
